/**
 * Just enough JPEG to keep a photo's EXIF through a re-encode, and to read its
 * size without decoding it.
 *
 * A canvas writes a bare JPEG: whatever the camera recorded — when the picture
 * was taken, where (GPS), with what — is gone. For an excavation photo that is
 * documentation in its own right, so the original's EXIF segment is carried
 * over into the new file, adjusted to what the new pixels are.
 *
 * And where the camera left out WHERE — Android's camera, opened from a web
 * page, writes no location — the app's own fix is written in (see
 * {@link withPosition}).
 */

const SOI = 0xd8;
const SOS = 0xda;
const EOI = 0xd9;
const APP0 = 0xe0;
const APP1 = 0xe1;

/** EXIF tags this module reads or rewrites. */
const TAG_ORIENTATION = 0x0112;
const TAG_EXIF_IFD = 0x8769;
const TAG_PIXEL_X = 0xa002;
const TAG_PIXEL_Y = 0xa003;
const TAG_GPS_IFD = 0x8825;

const TYPE_BYTE = 1;
const TYPE_ASCII = 2;
const TYPE_SHORT = 3;
const TYPE_LONG = 4;
const TYPE_RATIONAL = 5;

/** "Exif\0\0" — what an APP1 segment holding EXIF starts with. */
const EXIF_HEADER = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00];

interface Segment {
    marker: number;
    /** Offset of the 0xFF that starts the segment. */
    start: number;
    /** Offset just past the segment. */
    end: number;
}

/**
 * The header segments, up to the image data. Markers without a length (the
 * restart markers, padding) do not occur before the scan in a valid file;
 * anything that does not parse ends the walk.
 */
function headerSegments(bytes: Uint8Array): Segment[] {
    if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== SOI) {
        return [];
    }
    const segments: Segment[] = [];
    let offset = 2;
    while (offset + 4 <= bytes.length) {
        if (bytes[offset] !== 0xff) {
            break;
        }
        const marker = bytes[offset + 1];
        if (marker === 0xff) {
            // Fill byte before a marker.
            offset += 1;
            continue;
        }
        if (marker === SOS || marker === EOI) {
            break;
        }
        const length = (bytes[offset + 2] << 8) | bytes[offset + 3];
        const end = offset + 2 + length;
        if (length < 2 || end > bytes.length) {
            break;
        }
        segments.push({ marker, start: offset, end });
        offset = end;
    }
    return segments;
}

function isStartOfFrame(marker: number): boolean {
    // SOF0..SOF15, minus DHT (C4), JPG (C8) and DAC (CC).
    return (
        marker >= 0xc0 &&
        marker <= 0xcf &&
        marker !== 0xc4 &&
        marker !== 0xc8 &&
        marker !== 0xcc
    );
}

/** The stored pixel size, read from the frame header — no decoding. */
export function jpegDimensions(
    bytes: Uint8Array
): { width: number; height: number } | null {
    const frame = headerSegments(bytes).find((segment) =>
        isStartOfFrame(segment.marker)
    );
    if (!frame || frame.end - frame.start < 9) {
        return null;
    }
    const at = frame.start + 4;
    const height = (bytes[at + 1] << 8) | bytes[at + 2];
    const width = (bytes[at + 3] << 8) | bytes[at + 4];
    return width > 0 && height > 0 ? { width, height } : null;
}

function isExifSegment(bytes: Uint8Array, segment: Segment): boolean {
    return (
        segment.marker === APP1 &&
        EXIF_HEADER.every(
            (value, index) => bytes[segment.start + 4 + index] === value
        )
    );
}

/** The whole EXIF APP1 segment (marker and length included), if there is one. */
export function exifSegment(bytes: Uint8Array): Uint8Array | null {
    const segment = headerSegments(bytes).find((candidate) =>
        isExifSegment(bytes, candidate)
    );
    return segment ? bytes.slice(segment.start, segment.end) : null;
}

/**
 * A view on the TIFF structure inside an EXIF segment, in its own byte order.
 * Offsets are relative to the TIFF header, as EXIF's own are.
 */
function tiffReader(segment: Uint8Array) {
    const base = 4 + EXIF_HEADER.length;
    const view = new DataView(
        segment.buffer,
        segment.byteOffset,
        segment.byteLength
    );
    if (segment.length < base + 8) {
        return null;
    }
    const order = view.getUint16(base);
    const little = order === 0x4949;
    if (!little && order !== 0x4d4d) {
        return null;
    }
    const inRange = (offset: number, size: number) =>
        offset >= 0 && base + offset + size <= segment.length;
    return {
        view,
        base,
        little,
        inRange,
        u16: (offset: number) => view.getUint16(base + offset, little),
        u32: (offset: number) => view.getUint32(base + offset, little),
        firstIfd: view.getUint32(base + 4, little)
    };
}

type TiffReader = NonNullable<ReturnType<typeof tiffReader>>;

/** Offsets of the 12-byte entries of the IFD at `ifd`, by tag. */
function ifdEntries(tiff: TiffReader, ifd: number): Map<number, number> {
    const entries = new Map<number, number>();
    if (!tiff.inRange(ifd, 2)) {
        return entries;
    }
    const count = tiff.u16(ifd);
    for (let i = 0; i < count; i++) {
        const entry = ifd + 2 + i * 12;
        if (!tiff.inRange(entry, 12)) {
            break;
        }
        entries.set(tiff.u16(entry), entry);
    }
    return entries;
}

/** EXIF orientation 1..8; 1 (upright) when absent or unreadable. */
export function exifOrientation(segment: Uint8Array | null): number {
    const tiff = segment ? tiffReader(segment) : null;
    if (!tiff) {
        return 1;
    }
    const entry = ifdEntries(tiff, tiff.firstIfd).get(TAG_ORIENTATION);
    if (entry === undefined) {
        return 1;
    }
    const value = tiff.u16(entry + 8);
    return value >= 1 && value <= 8 ? value : 1;
}

/** Overwrite a SHORT or LONG entry's value in place; other types are left. */
function writeNumber(tiff: TiffReader, entry: number, value: number) {
    const type = tiff.u16(entry + 2);
    const at = tiff.base + entry + 8;
    if (type === TYPE_SHORT) {
        tiff.view.setUint16(at, value, tiff.little);
    } else if (type === TYPE_LONG) {
        tiff.view.setUint32(at, value, tiff.little);
    }
}

/**
 * The EXIF segment as it has to read on the RE-ENCODED image: upright, because
 * the decode already turned the pixels (a viewer honouring the old tag would
 * turn them a second time), and at the new pixel size. Everything else — the
 * time, the GPS position, the camera — goes over untouched.
 */
export function exifForReencoded(
    segment: Uint8Array,
    size: { width: number; height: number }
): Uint8Array {
    const copy = segment.slice();
    const tiff = tiffReader(copy);
    if (!tiff) {
        return copy;
    }
    const top = ifdEntries(tiff, tiff.firstIfd);
    const orientation = top.get(TAG_ORIENTATION);
    if (orientation !== undefined) {
        writeNumber(tiff, orientation, 1);
    }
    // The embedded preview (IFD1) still shows the camera's pixels, turned the
    // old way — an upright tag over it would show it on its side. Unlinked,
    // a viewer makes its own from the image; its bytes just go unread.
    const nextIfd = tiff.firstIfd + 2 + tiff.u16(tiff.firstIfd) * 12;
    if (tiff.inRange(nextIfd, 4)) {
        tiff.view.setUint32(tiff.base + nextIfd, 0, tiff.little);
    }
    const exifPointer = top.get(TAG_EXIF_IFD);
    if (exifPointer !== undefined) {
        const exif = ifdEntries(tiff, tiff.u32(exifPointer + 8));
        const pixelX = exif.get(TAG_PIXEL_X);
        const pixelY = exif.get(TAG_PIXEL_Y);
        if (pixelX !== undefined) {
            writeNumber(tiff, pixelX, size.width);
        }
        if (pixelY !== undefined) {
            writeNumber(tiff, pixelY, size.height);
        }
    }
    return copy;
}

/** Where a photo was taken, as the device's location service reported it. */
export interface PhotoPosition {
    latitude: number;
    longitude: number;
    /** Metres above the WGS84 ellipsoid; null when the fix had none. */
    altitude: number | null;
    /** Horizontal accuracy in metres; null when unknown. */
    accuracy: number | null;
    /** When the fix was taken, ms since the epoch. */
    timestamp: number;
}

/**
 * The position in an EXIF segment, or null without one. A GPS block holding
 * 0/0 counts as none: that is what Android leaves when it withholds the
 * location, and no excavation sits at the zero point in the Atlantic.
 */
export function exifPosition(
    segment: Uint8Array | null
): { latitude: number; longitude: number } | null {
    const tiff = segment ? tiffReader(segment) : null;
    if (!tiff) {
        return null;
    }
    const pointer = ifdEntries(tiff, tiff.firstIfd).get(TAG_GPS_IFD);
    if (pointer === undefined) {
        return null;
    }
    const gps = ifdEntries(tiff, tiff.u32(pointer + 8));
    const coordinate = (refTag: number, valueTag: number, negative: string) => {
        const entry = gps.get(valueTag);
        if (
            entry === undefined ||
            tiff.u16(entry + 2) !== TYPE_RATIONAL ||
            tiff.u32(entry + 4) !== 3
        ) {
            return null;
        }
        const at = tiff.u32(entry + 8);
        if (!tiff.inRange(at, 24)) {
            return null;
        }
        let degrees = 0;
        for (let i = 0; i < 3; i++) {
            const denominator = tiff.u32(at + i * 8 + 4);
            if (denominator !== 0) {
                degrees += tiff.u32(at + i * 8) / denominator / 60 ** i;
            }
        }
        const ref = gps.get(refTag);
        const sign =
            ref !== undefined &&
            String.fromCharCode(tiff.view.getUint8(tiff.base + ref + 8)) ===
                negative
                ? -1
                : 1;
        return degrees * sign;
    };
    const latitude = coordinate(1, 2, 'S');
    const longitude = coordinate(3, 4, 'W');
    if (latitude === null || longitude === null) {
        return null;
    }
    if (latitude === 0 && longitude === 0) {
        return null;
    }
    return { latitude, longitude };
}

/** An empty TIFF structure: header, IFD0 without entries. */
function emptyTiff(): Uint8Array {
    const tiff = new DataView(new ArrayBuffer(14));
    tiff.setUint16(0, 0x4949);
    tiff.setUint16(2, 42, true);
    tiff.setUint32(4, 8, true);
    return new Uint8Array(tiff.buffer);
}

/** A non-negative number as an EXIF RATIONAL, to the given denominator. */
function rational(value: number, denominator: number): [number, number] {
    return [Math.round(Math.abs(value) * denominator), denominator];
}

/** Degrees as the three RATIONALs of degrees, minutes and seconds. */
function degreesMinutesSeconds(value: number): [number, number][] {
    const absolute = Math.abs(value);
    const degrees = Math.floor(absolute);
    const minutes = Math.floor((absolute - degrees) * 60);
    const seconds = (absolute - degrees - minutes / 60) * 3600;
    return [[degrees, 1], [minutes, 1], rational(seconds, 10000)];
}

/**
 * `jpeg` with `position` written into its EXIF as a GPS block. What the EXIF
 * already holds stays as it was; a GPS block there (Android's empty one) is
 * replaced. Without EXIF, one is made that holds only the position.
 *
 * The TIFF structure is extended, not rewritten: a new IFD0 — the old entries
 * plus the pointer to the new GPS IFD — and the GPS IFD go at its end, and the
 * header points at the new IFD0. Every offset in the old data is relative to
 * the TIFF header and therefore stays valid.
 */
export function withPosition(
    jpeg: Uint8Array,
    position: PhotoPosition
): Uint8Array<ArrayBuffer> {
    const existing = exifSegment(jpeg);
    const existingTiff = existing ? tiffReader(existing) : null;
    const oldTiff =
        existing && existingTiff
            ? existing.subarray(existingTiff.base)
            : emptyTiff();
    const old = tiffReader(withExifHeader(oldTiff))!;

    const keptEntries = [...ifdEntries(old, old.firstIfd)]
        .filter(([tag]) => tag !== TAG_GPS_IFD)
        .map(([tag, entry]) => ({
            tag,
            bytes: oldTiff.subarray(entry, entry + 12)
        }));
    const nextIfd = old.firstIfd + 2 + old.u16(old.firstIfd) * 12;
    const nextIfdOffset = old.inRange(nextIfd, 4) ? old.u32(nextIfd) : 0;

    const time = new Date(position.timestamp);
    const pad = (value: number) => String(value).padStart(2, '0');
    const dateStamp = `${time.getUTCFullYear()}:${pad(time.getUTCMonth() + 1)}:${pad(time.getUTCDate())}\0`;
    type GpsEntry = {
        tag: number;
        type: number;
        count: number;
        bytes?: number[];
        ascii?: string;
        rationals?: [number, number][];
    };
    const gpsEntries: GpsEntry[] = [
        { tag: 0x00, type: TYPE_BYTE, count: 4, bytes: [2, 3, 0, 0] },
        {
            tag: 0x01,
            type: TYPE_ASCII,
            count: 2,
            ascii: position.latitude < 0 ? 'S\0' : 'N\0'
        },
        {
            tag: 0x02,
            type: TYPE_RATIONAL,
            count: 3,
            rationals: degreesMinutesSeconds(position.latitude)
        },
        {
            tag: 0x03,
            type: TYPE_ASCII,
            count: 2,
            ascii: position.longitude < 0 ? 'W\0' : 'E\0'
        },
        {
            tag: 0x04,
            type: TYPE_RATIONAL,
            count: 3,
            rationals: degreesMinutesSeconds(position.longitude)
        }
    ];
    if (position.altitude !== null) {
        gpsEntries.push(
            {
                tag: 0x05,
                type: TYPE_BYTE,
                count: 1,
                bytes: [position.altitude < 0 ? 1 : 0]
            },
            {
                tag: 0x06,
                type: TYPE_RATIONAL,
                count: 1,
                rationals: [rational(position.altitude, 100)]
            }
        );
    }
    gpsEntries.push(
        {
            tag: 0x07,
            type: TYPE_RATIONAL,
            count: 3,
            rationals: [
                [time.getUTCHours(), 1],
                [time.getUTCMinutes(), 1],
                [time.getUTCSeconds(), 1]
            ]
        },
        { tag: 0x1d, type: TYPE_ASCII, count: 11, ascii: dateStamp }
    );
    if (position.accuracy !== null) {
        gpsEntries.push({
            tag: 0x1f,
            type: TYPE_RATIONAL,
            count: 1,
            rationals: [rational(position.accuracy, 100)]
        });
    }

    // Offsets in the TIFF structure, all kept even as TIFF wants them.
    const even = (offset: number) => offset + (offset % 2);
    const ifd0At = even(oldTiff.length);
    const ifd0Count = keptEntries.length + 1;
    const gpsAt = ifd0At + 2 + ifd0Count * 12 + 4;
    let dataAt = gpsAt + 2 + gpsEntries.length * 12 + 4;
    // What does not fit into an entry's four value bytes goes behind the IFD.
    const dataSize = gpsEntries.reduce((sum, entry) => {
        if (entry.rationals) {
            return sum + entry.rationals.length * 8;
        }
        if (entry.ascii !== undefined && entry.ascii.length > 4) {
            return sum + even(entry.ascii.length);
        }
        return sum;
    }, 0);
    const total = dataAt + dataSize;
    // An APP1 segment's length is 16 bits; it would not fit.
    if (total + 2 + EXIF_HEADER.length > 0xffff) {
        return jpeg.slice();
    }

    const tiff = new Uint8Array(total);
    tiff.set(oldTiff);
    const view = new DataView(tiff.buffer);
    const little = old.little;
    const u16 = (at: number, value: number) =>
        view.setUint16(at, value, little);
    const u32 = (at: number, value: number) =>
        view.setUint32(at, value, little);

    u32(4, ifd0At);
    const ifd0 = [
        ...keptEntries,
        { tag: TAG_GPS_IFD, bytes: null as Uint8Array | null }
    ].sort((first, second) => first.tag - second.tag);
    u16(ifd0At, ifd0Count);
    ifd0.forEach((entry, index) => {
        const at = ifd0At + 2 + index * 12;
        if (entry.bytes) {
            tiff.set(entry.bytes, at);
            return;
        }
        u16(at, TAG_GPS_IFD);
        u16(at + 2, TYPE_LONG);
        u32(at + 4, 1);
        u32(at + 8, gpsAt);
    });
    u32(ifd0At + 2 + ifd0Count * 12, nextIfdOffset);

    u16(gpsAt, gpsEntries.length);
    gpsEntries.forEach((entry, index) => {
        const at = gpsAt + 2 + index * 12;
        u16(at, entry.tag);
        u16(at + 2, entry.type);
        u32(at + 4, entry.count);
        if (entry.bytes) {
            tiff.set(entry.bytes, at + 8);
        } else if (entry.ascii !== undefined && entry.ascii.length <= 4) {
            for (let i = 0; i < entry.ascii.length; i++) {
                tiff[at + 8 + i] = entry.ascii.charCodeAt(i);
            }
        } else {
            u32(at + 8, dataAt);
            if (entry.ascii !== undefined) {
                for (let i = 0; i < entry.ascii.length; i++) {
                    tiff[dataAt + i] = entry.ascii.charCodeAt(i);
                }
                dataAt += even(entry.ascii.length);
            } else {
                for (const [numerator, denominator] of entry.rationals ?? []) {
                    u32(dataAt, numerator);
                    u32(dataAt + 4, denominator);
                    dataAt += 8;
                }
            }
        }
    });
    u32(gpsAt + 2 + gpsEntries.length * 12, 0);

    return withExif(jpeg, withExifHeader(tiff));
}

/** A TIFF structure wrapped as an EXIF APP1 segment. */
function withExifHeader(tiff: Uint8Array): Uint8Array {
    const length = 2 + EXIF_HEADER.length + tiff.length;
    const segment = new Uint8Array(2 + length);
    segment.set([0xff, APP1, length >> 8, length & 0xff, ...EXIF_HEADER]);
    segment.set(tiff, 4 + EXIF_HEADER.length);
    return segment;
}

/**
 * `jpeg` with `segment` as its EXIF: after the JFIF header where there is one
 * (APP0 is meant to come first), else right after the start marker. An EXIF
 * segment already in `jpeg` is replaced.
 */
export function withExif(
    jpeg: Uint8Array,
    segment: Uint8Array
): Uint8Array<ArrayBuffer> {
    const segments = headerSegments(jpeg);
    if (segments.length === 0 && !(jpeg[0] === 0xff && jpeg[1] === SOI)) {
        return jpeg.slice();
    }
    const jfif = segments.find((candidate) => candidate.marker === APP0);
    const insertAt = jfif && jfif.start === 2 ? jfif.end : 2;
    const parts: Uint8Array[] = [jpeg.subarray(0, insertAt), segment];
    let offset = insertAt;
    for (const existing of segments) {
        if (existing.start < insertAt || !isExifSegment(jpeg, existing)) {
            continue;
        }
        parts.push(jpeg.subarray(offset, existing.start));
        offset = existing.end;
    }
    parts.push(jpeg.subarray(offset));
    const result = new Uint8Array(
        parts.reduce((sum, part) => sum + part.length, 0)
    );
    let position = 0;
    for (const part of parts) {
        result.set(part, position);
        position += part.length;
    }
    return result;
}
